import { inject, Service, DestroyRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';

@Service()
export class I18nTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private currentTitleKey?: string;
  private translationsLoaded = toSignal(
    this.transloco.events$.pipe(
      filter((event: { type: string }) => event.type === 'translationLoadSuccess'),
      map(() => true),
    ),
    { initialValue: false },
  );

  constructor() {
    super();

    // Auto update title when language changes
    this.transloco.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.currentTitleKey) {
        this.setTranslatedTitle(this.currentTitleKey);
      }
    });

    // Update title when translations are loaded (if we have a pending title)
    this.transloco.events$
      .pipe(
        filter((event: { type: string }) => event.type === 'translationLoadSuccess'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.currentTitleKey) {
          this.setTranslatedTitle(this.currentTitleKey);
        }
      });
  }

  override updateTitle(routerState: RouterStateSnapshot): void {
    const titleKey = this.buildTitle(routerState);

    if (!titleKey) return;

    this.currentTitleKey = titleKey;

    // If translations are already loaded, set title immediately
    if (this.translationsLoaded()) {
      this.setTranslatedTitle(titleKey);
    }
  }

  private setTranslatedTitle(key: string): void {
    const translated = this.transloco.translate(key);
    this.title.setTitle(translated);
  }
}
